import React from "react";

function readEnv(name, fallback = "") {
  return process.env[name] || fallback;
}

export default function FlipToLivePanel({ metrics }) {
  const mode = readEnv("REACT_APP_BILLING_ALERTS_MODE", "TEST");
  const p95Target = Number(readEnv("REACT_APP_BILLING_ALERTS_P95_MS", "2000"));
  const notif =
    readEnv("REACT_APP_NOTIFICATIONS_CONFIGURED", "false") === "true";
  const stripeLiveReady = Boolean(readEnv("REACT_APP_STRIPE_SECRET_KEY", ""));

  const gates = [
    {
      label: `Monitoring p95 < ${p95Target} ms`,
      ok: metrics.p95Ms < p95Target,
      note: `${metrics.p95Ms} ms`,
    },
    {
      label: "Timeout rate < 5%",
      ok: metrics.timeoutRatePct < 5,
      note: `${metrics.timeoutRatePct.toFixed(1)}%`,
    },
    {
      label: "Notifications configured",
      ok: notif,
    },
    {
      label: "Disclaimer live on public pages",
      ok: metrics.disclaimerOk,
    },
    {
      label: "Current mode is TEST (burn-in)",
      ok: mode === "TEST",
    },
    {
      label: "Stripe LIVE keys present (only if charging now)",
      ok: stripeLiveReady,
      note: stripeLiveReady ? "ready" : "optional until charging",
    },
  ];

  const allGreen = gates.every(
    (g) => g.ok || g.label.includes("Stripe LIVE keys"),
  );

  const liveEnvBlock = [
    "BILLING_ALERTS_MODE=LIVE",
    `BILLING_ALERTS_P95_MS=${p95Target}`,
    `NOTIFICATIONS_CONFIGURED=${notif ? "true" : "false"}`,
    "# Include Stripe only if you plan to charge at launch",
    "STRIPE_SECRET_KEY=<live_key_here>",
    "STARTER_PRICE_ID=<price_live_starter>",
    "PRO_PRICE_ID=<price_live_pro>",
    `LEGAL_LAST_UPDATED=${readEnv("REACT_APP_LEGAL_LAST_UPDATED", "September 28, 2025")}`,
  ].join("\n");

  const handleCopyToClipboard = () => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(liveEnvBlock)
        .then(() => {
          // Could add a toast notification here if desired
          console.log("LIVE environment configuration copied to clipboard");
        })
        .catch((err) => {
          console.error("Failed to copy to clipboard:", err);
        });
    } else {
      // Fallback for browsers that don't support clipboard API
      console.log("Clipboard API not supported");
    }
  };

  return (
    <div className="rounded-2xl border p-4 shadow-sm space-y-3">
      <h3 className="text-lg font-semibold">Flip to LIVE — Preflight</h3>
      <ul className="space-y-1">
        {gates.map((g, i) => (
          <li key={i} className="flex items-center">
            <span
              className={`mr-2 h-2 w-2 rounded-full ${g.ok ? "bg-green-500" : "bg-yellow-500"}`}
            />
            <span className="text-sm">
              {g.label}
              {g.note ? ` — ${g.note}` : ""}
            </span>
          </li>
        ))}
      </ul>
      <div className="text-sm">
        <p className="mt-2">
          {allGreen
            ? "✅ All critical gates are green. Set the following envs, redeploy, and re-run the smoke test."
            : "⚠️ Some gates are not green yet. Resolve the yellow items before flipping to LIVE."}
        </p>
        <textarea
          readOnly
          className="mt-2 w-full border rounded p-2 text-xs font-mono bg-gray-50"
          rows={8}
          value={liveEnvBlock}
        />
        <button
          type="button"
          onClick={handleCopyToClipboard}
          className="mt-2 inline-flex items-center rounded px-3 py-1.5 text-sm border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          📋 Copy LIVE Env
        </button>
        <p className="text-xs text-gray-600 mt-2">
          <strong>Rollback:</strong> set <code>BILLING_ALERTS_MODE=TEST</code>{" "}
          and redeploy previous tag. Keep logs for post-mortem.
        </p>
      </div>
    </div>
  );
}
